import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Button,
  TextField,
  InputAdornment,
  Stack,
  Alert,
  LinearProgress,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from '@mui/material';
import {
  Search,
  FilterList,
  Visibility,
  Block,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  AccountBalance,
  Receipt,
  People,
  Refresh,
  Download,
  Close,
  ContentCopy
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { BrowserProvider, Contract, formatEther } from 'ethers';
import { useBlockedUsers } from '../../context/BlockedUsersProviderIPFS';

// Import contract ABIs
import marketplaceContract from '../abis/CarbonCreditMarketplace.json';
import nftContract from '../abis/CarbonCreditNFT.json';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

const MARKETPLACE_ADDRESS = marketplaceContract.networks[process.env.REACT_APP_NETWORK_ID]?.address;
const NFT_ADDRESS = nftContract.networks[process.env.REACT_APP_NETWORK_ID]?.address;

const AdminUserAnalytics = ({
  account,
  isAdmin,
  provider,
  signer,
  vault,
  nft,
  carbon,
  marketplace,
  vaultAddress,
  nftAddress,
  carbonAddress,
  marketplaceAddress,
  history,
  retiredEvents,
  enqueueSnackbar,
  refreshVaultStatus
} = {}) => {
  const { isUserBlocked, toggleUserBlock } = useBlockedUsers();
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('volume');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(20);

  const [userAnalytics, setUserAnalytics] = useState({
    totalUsers: 0,
    activeUsers: 0,
    newUsersThisMonth: 0,
    averageUserValue: 0,
    topSpender: null,
    mostActiveTrader: null,
    retentionRate: 0
  });

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [userGrowthChart, setUserGrowthChart] = useState({ labels: [], datasets: [] });
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailsDialogOpen, setUserDetailsDialogOpen] = useState(false);

  useEffect(() => {
    fetchUserData();
  }, []);

  useEffect(() => {
    filterAndSortUsers();
  }, [users, searchTerm, filterStatus, sortBy, sortOrder]);

  const fetchUserData = async () => {
    setLoading(true);
    try {
      // Try to use passed props first, fallback to creating new connection
      let currentProvider = provider;
      let currentMarketplace = marketplace;
      let currentNft = nft;

      if (!currentProvider || !currentMarketplace || !currentNft) {
        if (!window.ethereum) {
          throw new Error('No wallet connected');
        }
        
        currentProvider = new BrowserProvider(window.ethereum);
        currentMarketplace = new Contract(
          marketplaceAddress || MARKETPLACE_ADDRESS, 
          marketplaceContract.abi, 
          currentProvider
        );
        currentNft = new Contract(
          nftAddress || NFT_ADDRESS, 
          nftContract.abi, 
          currentProvider
        );
      }

      // Fetch all marketplace events to build user profiles
      const [
        purchaseEvents,
        listingEvents,
        auctionEvents,
        bidEvents,
        retirementEvents
      ] = await Promise.all([
        currentMarketplace.queryFilter(currentMarketplace.filters.TokenPurchased(null)),
        currentMarketplace.queryFilter(currentMarketplace.filters.TokenListed(null)),
        currentMarketplace.queryFilter(currentMarketplace.filters.AuctionEnded(null)),
        currentMarketplace.queryFilter(currentMarketplace.filters.NewHighestBid(null)),
        currentNft.queryFilter(currentNft.filters.CarbonCreditRetired(null))
      ]);

      console.log('Fetched user events:', {
        purchases: purchaseEvents.length,
        listings: listingEvents.length,
        auctions: auctionEvents.length,
        bids: bidEvents.length,
        retirements: retirementEvents.length
      });

      // Build comprehensive user profiles
      const userMap = new Map();

      // Process purchase events
      for (const event of purchaseEvents) {
        const buyer = event.args.buyer;
        const price = parseFloat(formatEther(event.args.price || '0'));
        const block = await provider.getBlock(event.blockNumber);
        
        if (!userMap.has(buyer)) {
          userMap.set(buyer, {
            address: buyer,
            totalSpent: 0,
            totalEarned: 0,
            purchases: 0,
            sales: 0,
            listings: 0,
            bids: 0,
            retirements: 0,
            firstSeen: block.timestamp,
            lastActivity: block.timestamp,
            avgPurchaseValue: 0,
            status: 'active',
            riskScore: 0,
            engagement: 0
          });
        }

        const user = userMap.get(buyer);
        user.totalSpent += price;
        user.purchases++;
        user.lastActivity = Math.max(user.lastActivity, block.timestamp);
        user.avgPurchaseValue = user.totalSpent / user.purchases;
      }

      // Process listing events (sellers)
      for (const event of listingEvents) {
        const seller = event.args.seller;
        const block = await provider.getBlock(event.blockNumber);
        
        if (!userMap.has(seller)) {
          userMap.set(seller, {
            address: seller,
            totalSpent: 0,
            totalEarned: 0,
            purchases: 0,
            sales: 0,
            listings: 0,
            bids: 0,
            retirements: 0,
            firstSeen: block.timestamp,
            lastActivity: block.timestamp,
            avgPurchaseValue: 0,
            status: 'active',
            riskScore: 0,
            engagement: 0
          });
        }

        const user = userMap.get(seller);
        user.listings++;
        user.lastActivity = Math.max(user.lastActivity, block.timestamp);
      }

      // Process sales from purchase events (seller earnings)
      for (const event of purchaseEvents) {
        const seller = event.args.seller;
        if (seller && userMap.has(seller)) {
          const price = parseFloat(formatEther(event.args.price || '0'));
          const user = userMap.get(seller);
          user.totalEarned += price * 0.975; // Assuming 2.5% marketplace fee
          user.sales++;
        }
      }

      // Process bid events
      for (const event of bidEvents) {
        const bidder = event.args.bidder;
        const block = await provider.getBlock(event.blockNumber);
        
        if (userMap.has(bidder)) {
          const user = userMap.get(bidder);
          user.bids++;
          user.lastActivity = Math.max(user.lastActivity, block.timestamp);
        }
      }

      // Process retirement events
      for (const event of retirementEvents) {
        const retiredBy = event.args.retiredBy;
        const block = await provider.getBlock(event.blockNumber);
        
        if (userMap.has(retiredBy)) {
          const user = userMap.get(retiredBy);
          user.retirements++;
          user.lastActivity = Math.max(user.lastActivity, block.timestamp);
        }
      }

      // Calculate engagement scores and risk assessments
      const now = Math.floor(Date.now() / 1000);
      const usersArray = Array.from(userMap.values()).map(user => {
        const daysSinceLastActivity = (now - user.lastActivity) / (24 * 3600);
        const totalActivity = user.purchases + user.sales + user.listings + user.bids + user.retirements;
        
        // Engagement score (0-100)
        user.engagement = Math.min(100, Math.floor(
          (totalActivity * 10) + 
          (user.totalSpent * 5) + 
          (user.retirements * 20) +
          Math.max(0, 50 - daysSinceLastActivity)
        ));

        // Risk score (0-100, higher = more risk)
        user.riskScore = Math.min(100, Math.floor(
          (daysSinceLastActivity > 30 ? 30 : 0) +
          (user.purchases === 0 && user.listings > 5 ? 20 : 0) +
          (user.avgPurchaseValue > 10 ? 15 : 0) +
          (totalActivity < 2 ? 25 : 0)
        ));

        // Update status based on activity
        if (daysSinceLastActivity > 60) {
          user.status = 'inactive';
        } else if (daysSinceLastActivity > 30) {
          user.status = 'dormant';
        } else {
          user.status = 'active';
        }

        // Check if user is blocked (override other statuses)
        if (isUserBlocked(user.address)) {
          user.status = 'blocked';
        }

        return user;
      });

      setUsers(usersArray);

      // Calculate analytics
      const totalUsers = usersArray.length;
      const activeUsers = usersArray.filter(u => u.status === 'active').length;
      const thisMonth = now - (30 * 24 * 3600);
      const newUsersThisMonth = usersArray.filter(u => u.firstSeen > thisMonth).length;
      const totalValue = usersArray.reduce((sum, u) => sum + u.totalSpent, 0);
      const averageUserValue = totalValue / totalUsers;

      const topSpender = usersArray.reduce((top, user) => 
        user.totalSpent > (top?.totalSpent || 0) ? user : top, null);
      
      const mostActiveTrader = usersArray.reduce((top, user) => 
        (user.purchases + user.sales) > ((top?.purchases || 0) + (top?.sales || 0)) ? user : top, null);

      setUserAnalytics({
        totalUsers,
        activeUsers,
        newUsersThisMonth,
        averageUserValue,
        topSpender,
        mostActiveTrader,
        retentionRate: (activeUsers / totalUsers) * 100
      });

      // Generate user growth chart
      generateUserGrowthChart(usersArray);

    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateUserGrowthChart = (usersArray) => {
    // Group users by month of first activity
    const monthlyGrowth = {};
    const now = new Date();
    
    // Initialize last 12 months
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      monthlyGrowth[key] = 0;
    }

    // Count new users by month
    usersArray.forEach(user => {
      const date = new Date(user.firstSeen * 1000);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (monthlyGrowth.hasOwnProperty(key)) {
        monthlyGrowth[key]++;
      }
    });

    const labels = Object.keys(monthlyGrowth).map(key => {
      const [year, month] = key.split('-');
      return new Date(year, month - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    });

    const data = Object.values(monthlyGrowth);

    setUserGrowthChart({
      labels,
      datasets: [
        {
          label: 'New Users',
          data,
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          tension: 0.1,
          fill: true
        }
      ]
    });
  };

  const filterAndSortUsers = () => {
    let filtered = [...users];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.address.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(user => user.status === filterStatus);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'volume':
          aValue = a.totalSpent + a.totalEarned;
          bValue = b.totalSpent + b.totalEarned;
          break;
        case 'engagement':
          aValue = a.engagement;
          bValue = b.engagement;
          break;
        case 'risk':
          aValue = a.riskScore;
          bValue = b.riskScore;
          break;
        case 'activity':
          aValue = a.purchases + a.sales + a.listings + a.bids;
          bValue = b.purchases + b.sales + b.listings + b.bids;
          break;
        case 'lastActivity':
          aValue = a.lastActivity;
          bValue = b.lastActivity;
          break;
        default:
          aValue = a.totalSpent;
          bValue = b.totalSpent;
      }

      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    });

    setFilteredUsers(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleExportUsers = () => {
    const exportData = filteredUsers.map(user => ({
      address: user.address,
      totalSpent: user.totalSpent,
      totalEarned: user.totalEarned,
      purchases: user.purchases,
      sales: user.sales,
      listings: user.listings,
      retirements: user.retirements,
      engagement: user.engagement,
      riskScore: user.riskScore,
      status: user.status,
      firstSeen: new Date(user.firstSeen * 1000).toISOString(),
      lastActivity: new Date(user.lastActivity * 1000).toISOString()
    }));

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `user-analytics-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleViewUserDetails = (user) => {
    setSelectedUser(user);
    setUserDetailsDialogOpen(true);
  };

  const handleCloseUserDetailsDialog = () => {
    setUserDetailsDialogOpen(false);
    setSelectedUser(null);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      if (enqueueSnackbar) {
        enqueueSnackbar('Copied to clipboard!', { variant: 'success' });
      }
    });
  };

  const handleBlockUser = (user) => {
    // Use the global blocked users context
    const wasBlocked = toggleUserBlock(user.address);
    
    if (enqueueSnackbar) {
      const action = wasBlocked ? 'blocked' : 'unblocked';
      enqueueSnackbar(`User ${user.address.slice(0, 6)}...${user.address.slice(-4)} has been ${action}`, { 
        variant: wasBlocked ? 'warning' : 'success' 
      });
    }

    // Update the local users state to reflect the change
    const updatedUsers = users.map(u => ({
      ...u,
      status: u.address === user.address 
        ? (wasBlocked ? 'blocked' : 'active') 
        : u.status
    }));
    
    setUsers(updatedUsers);
  };

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const startIndex = (currentPage - 1) * usersPerPage;
  const endIndex = startIndex + usersPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          User Analytics
        </Typography>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading user data...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          User Analytics
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchUserData}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleExportUsers}
          >
            Export Users
          </Button>
        </Stack>
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Users
                  </Typography>
                  <Typography variant="h5">
                    {userAnalytics.totalUsers.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {userAnalytics.newUsersThisMonth} new this month
                  </Typography>
                </Box>
                <People color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Active Users
                  </Typography>
                  <Typography variant="h5">
                    {userAnalytics.activeUsers.toLocaleString()}
                  </Typography>
                  <Chip
                    label={`${userAnalytics.retentionRate.toFixed(1)}% retention`}
                    color="success"
                    size="small"
                    icon={<TrendingUp />}
                  />
                </Box>
                <CheckCircle color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Avg User Value
                  </Typography>
                  <Typography variant="h5">
                    {userAnalytics.averageUserValue.toFixed(3)} ETH
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Per user lifetime value
                  </Typography>
                </Box>
                <AccountBalance color="secondary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Top Spender
                  </Typography>
                  <Typography variant="h6">
                    {userAnalytics.topSpender ? 
                      `${userAnalytics.topSpender.address.slice(0, 6)}...${userAnalytics.topSpender.address.slice(-4)}` 
                      : 'N/A'
                    }
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {userAnalytics.topSpender?.totalSpent.toFixed(3) || '0'} ETH spent
                  </Typography>
                </Box>
                <Receipt color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* User Growth Chart */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            User Growth Trend
          </Typography>
          <Box sx={{ height: 300 }}>
            <Line
              data={userGrowthChart}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                  y: {
                    beginAtZero: true,
                    title: {
                      display: true,
                      text: 'New Users'
                    }
                  }
                }
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Filters and Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <TextField
              placeholder="Search by address..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250 }}
            />

            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="dormant">Dormant</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="blocked">Blocked</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Sort by</InputLabel>
              <Select
                value={sortBy}
                label="Sort by"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="volume">Volume</MenuItem>
                <MenuItem value="engagement">Engagement</MenuItem>
                <MenuItem value="risk">Risk Score</MenuItem>
                <MenuItem value="activity">Activity</MenuItem>
                <MenuItem value="lastActivity">Last Activity</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              endIcon={sortOrder === 'asc' ? <TrendingUp /> : <TrendingDown />}
            >
              {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
            </Button>

            <Typography variant="body2" color="textSecondary" sx={{ ml: 'auto' }}>
              Showing {startIndex + 1}-{Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} users
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            User Details
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Total Spent</TableCell>
                  <TableCell>Total Earned</TableCell>
                  <TableCell>Purchases</TableCell>
                  <TableCell>Sales</TableCell>
                  <TableCell>Retirements</TableCell>
                  <TableCell>Engagement</TableCell>
                  <TableCell>Risk Score</TableCell>
                  <TableCell>Last Activity</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedUsers.map((user, index) => (
                  <TableRow key={user.address}>
                    <TableCell>
                      <Tooltip title={user.address}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {user.address.slice(0, 6)}...{user.address.slice(-4)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.status}
                        color={
                          user.status === 'active' ? 'success' :
                          user.status === 'dormant' ? 'warning' : 
                          user.status === 'blocked' ? 'error' : 'default'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{user.totalSpent.toFixed(4)} ETH</TableCell>
                    <TableCell>{user.totalEarned.toFixed(4)} ETH</TableCell>
                    <TableCell>{user.purchases}</TableCell>
                    <TableCell>{user.sales}</TableCell>
                    <TableCell>{user.retirements}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LinearProgress
                          variant="determinate"
                          value={user.engagement}
                          sx={{ width: 60, mr: 1 }}
                        />
                        <Typography variant="body2">{user.engagement}%</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.riskScore}
                        color={
                          user.riskScore < 30 ? 'success' :
                          user.riskScore < 60 ? 'warning' : 'error'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(user.lastActivity * 1000).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleViewUserDetails(user)}>
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={user.status === 'blocked' ? 'Unblock User' : 'Block User'}>
                        <IconButton 
                          size="small" 
                          color={user.status === 'blocked' ? 'success' : 'error'} 
                          onClick={() => handleBlockUser(user)}
                        >
                          {user.status === 'blocked' ? <CheckCircle /> : <Block />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(e, page) => setCurrentPage(page)}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* User Details Dialog */}
      <Dialog 
        open={userDetailsDialogOpen} 
        onClose={handleCloseUserDetailsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            User Details
            <IconButton onClick={handleCloseUserDetailsDialog}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {selectedUser && (
            <Box sx={{ py: 2 }}>
              {/* User Address */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  User Address
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {selectedUser.address}
                  </Typography>
                  <IconButton size="small" onClick={() => copyToClipboard(selectedUser.address)}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* User Stats Grid */}
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Status
                  </Typography>
                  <Chip
                    label={selectedUser.status}
                    color={
                      selectedUser.status === 'active' ? 'success' :
                      selectedUser.status === 'dormant' ? 'warning' : 
                      selectedUser.status === 'blocked' ? 'error' : 'default'
                    }
                    sx={{ mb: 2 }}
                  />

                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Total Spent
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedUser.totalSpent.toFixed(4)} ETH
                  </Typography>

                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Total Earned
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedUser.totalEarned.toFixed(4)} ETH
                  </Typography>

                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Net Position
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      mb: 2,
                      color: (selectedUser.totalEarned - selectedUser.totalSpent) >= 0 ? 'success.main' : 'error.main'
                    }}
                  >
                    {(selectedUser.totalEarned - selectedUser.totalSpent).toFixed(4)} ETH
                  </Typography>

                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Engagement Score
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LinearProgress
                      variant="determinate"
                      value={selectedUser.engagement}
                      sx={{ width: 100, mr: 1 }}
                    />
                    <Typography variant="body2">{selectedUser.engagement}%</Typography>
                  </Box>

                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Risk Score
                  </Typography>
                  <Chip
                    label={`${selectedUser.riskScore}/100`}
                    color={
                      selectedUser.riskScore < 30 ? 'success' :
                      selectedUser.riskScore < 60 ? 'warning' : 'error'
                    }
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Activity Summary
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Purchases:</strong> {selectedUser.purchases}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Sales:</strong> {selectedUser.sales}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Listings:</strong> {selectedUser.listings}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Bids:</strong> {selectedUser.bids}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Retirements:</strong> {selectedUser.retirements}
                    </Typography>
                  </Box>

                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Average Purchase Value
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedUser.avgPurchaseValue.toFixed(4)} ETH
                  </Typography>

                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    First Seen
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {new Date(selectedUser.firstSeen * 1000).toLocaleDateString()}
                  </Typography>

                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Last Activity
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {new Date(selectedUser.lastActivity * 1000).toLocaleDateString()}
                  </Typography>

                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Days Since Last Activity
                  </Typography>
                  <Typography variant="body1">
                    {Math.floor((Date.now() / 1000 - selectedUser.lastActivity) / (24 * 3600))} days
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseUserDetailsDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminUserAnalytics;
